/**
 * kb.ts — Cliente API da Base de Conhecimento do Atendente IA.
 *
 * Cobre as 7 rotas da spec §7:
 * - uploadKbDocument   — POST /assistant/kb/documents (multipart, com progresso)
 * - getKbLimites       — GET  /assistant/kb/limites (o teto de envio que a api aplica)
 * - listKbDocuments    — GET  /assistant/kb/documents
 * - getKbDocument      — GET  /assistant/kb/documents/{id}
 * - baixarKbInteiro    — GET  /assistant/kb/documents/{id}/download (PDF no padrão; só
 *   proprietário ou auditor, spec 2026-09-27-kb-visualizar-sem-copia §5, §9)
 * - baixarKbEvidencia  — GET  /assistant/kb/documents/{id}/evidencia (PDF do primeiro e do
 *   último terço; quem lê a pasta)
 * - getKbVisualizacao / getKbPagina — visualizador sem cópia (mesma spec, §7)
 * - updateKbDocument   — PATCH /assistant/kb/documents/{id}
 * - reindexKbDocument  — POST  /assistant/kb/documents/{id}/reindex
 * - deleteKbDocument   — DELETE /assistant/kb/documents/{id}
 *
 * As fontes do KB-Git (spec 2026-09-26 KB-Git, §11):
 * - listKbGitSources — `manage` ou `audit`
 * - mudarKbGitSource — excluir / reativar / regerar, `manage`
 *
 * Arquivo repetido e atalho (spec 2026-10-22-kb-arquivo-repetido-e-atalho, §4.3):
 * - verificarKbDocumento — POST /assistant/kb/documents/verificar (leitura, sem evento)
 * - criarKbAtalho / removerKbAtalho — `manage` na organização da pasta
 * - listarAtalhosDoDocumento — GET /assistant/kb/documents/{id}/atalhos, `manage`
 *
 * E as pastas (spec 2026-09-26, §6):
 * - listKbFolders / getKbFolder / getKbFolderDetalhes — leitura
 * - createKbFolder / updateKbFolder / setKbFolderPerfis / deleteKbFolder — `manage`
 */
import { api } from '@/api/client'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type KbDocumentStatus = 'pending' | 'indexing' | 'indexed' | 'failed' | 'disabled'

export type KbEnvironment = 'prd' | 'hml' | 'qa' | 'dev' | 'stg' | 'sandbox'

export interface KbDocument {
  id: number
  titulo: string
  descricao: string | null
  fonte: string | null
  mime: string
  status: KbDocumentStatus
  chunk_count: number
  organization_id: number | null
  environment: KbEnvironment | null
  tags: string[]
  truncated: boolean
  /** Motivo da falha (`failed`), ou o erro da última tentativa enquanto volta a `pending`. */
  erro: string | null
  created_at: string
  updated_at: string
  created_by_email: string | null
  /** Pasta onde o documento está (um documento, uma pasta — D6). */
  folder_id: number | null
  /** `git`: dossiê gerado de um repositório (spec KB-Git); `upload`: enviado por alguém. */
  origem?: 'upload' | 'git' | 'guia'
  /** Da fonte do Git que gerou o documento; null em upload (selo "Gerado do Git"). */
  git_commit?: string | null
  git_commit_em?: string | null
  git_commit_url?: string | null
  git_estado?: KbGitSourceEstado | null
}

// ---------------------------------------------------------------------------
// Arquivo repetido e atalho (spec 2026-10-22-kb-arquivo-repetido-e-atalho)
// ---------------------------------------------------------------------------

export type SituacaoDoArquivo = 'novo' | 'mesma_pasta' | 'atalho_na_pasta' | 'outra_pasta'

/** O documento que já tem o mesmo conteúdo na organização. */
export interface KbDocumentoExistente {
  id: number
  titulo: string
  pasta_id: number | null
  /** `guia` não muda de pasta: a tela não oferece "Mover o original para cá" */
  origem?: 'upload' | 'git' | 'guia'
  /** "A › B › Req1"; só vem quando a pasta do original é navegável para quem pergunta */
  caminho?: string
}

/** Resposta do `verificar` e `detail` do 409 `documento_ja_existe` do envio (mesma forma). */
export interface KbVerificacao {
  situacao: SituacaoDoArquivo
  documento?: KbDocumentoExistente
}

/** Atalho na pasta aberta. Quem lê é o original: `liberado` sai da pasta dele. */
export interface KbAtalho {
  id: number
  /** `document_id`, `status`, `mime` e `pasta_original.id` vêm `null` para quem não lê
   * o original e não gere a organização: a linha não conta nada do documento. */
  document_id: number | null
  /** `null` quando a pessoa não lê o original e a pasta dele é sensível */
  titulo: string | null
  status: KbDocumentStatus | null
  mime: string | null
  liberado: boolean
  /** `caminho` ("A › B › Req1") só vem quando a pasta do original é navegável */
  pasta_original: { id: number | null; caminho?: string }
}

/** Atalho visto do documento original. */
export interface KbAtalhoDoDocumento {
  id: number
  pasta_id: number
  /** "A › B › Req2"; só vem quando a pasta do atalho é navegável */
  caminho?: string
}

export interface KbAtalhoCriado {
  id: number
  document_id: number
  folder_id: number
}

// ---------------------------------------------------------------------------
// Fontes do KB-Git (spec 2026-09-26 KB-Git, §11)
// ---------------------------------------------------------------------------

export type KbGitSourceEstado = 'pendente' | 'atual' | 'falhou' | 'inativo' | 'pausado' | 'excluido'

export interface KbGitSource {
  id: number
  organization_id: number
  gitlab_project_id: number
  /** `path_with_namespace` do projeto; null até a primeira coleta. */
  caminho: string | null
  branch: string
  document_id: number | null
  ultimo_commit: string | null
  commit_em: string | null
  commit_url: string | null
  estado: KbGitSourceEstado
  erro: string | null
  tentativas: number
  /** `falhou` no teto de tentativas: a passada não a coleta mais até Regerar (backend decide). */
  bloqueada: boolean
  gerado_em: string | null
  updated_at: string | null
}

export type KbGitSourceOperacao = 'excluir' | 'reativar' | 'regerar'

export interface KbDocumentListResponse {
  items: KbDocument[]
  total: number
}

/** Metadados do visualizador (spec 2026-09-27-kb-visualizar-sem-copia §7.1). */
export interface KbVisualizacao {
  document_id: number
  titulo: string
  pasta: string | null
  /** 'pdf' ou o mime que ainda não tem visualização */
  formato: string
  visualizavel: boolean
  paginas: number | null
  /** Só proprietário (autor ou gestor da pasta) ou auditor */
  pode_baixar_inteiro: boolean
  evidencia: {
    disponivel: boolean
    /** [[1, 3], [8, 10]]: primeiro e último terço, em páginas */
    faixas: [number, number][] | null
    motivo: string | null
  }
}

export interface KbDocumentFilters {
  organization_id?: number | null
  environment?: KbEnvironment | null
  status?: KbDocumentStatus | null
  tag?: string | null
  /** Só os documentos desta pasta (a regra de leitura continua valendo) */
  folder_id?: number | null
}

export interface KbDocumentPatch {
  titulo?: string
  descricao?: string | null
  tags?: string[]
  environment?: KbEnvironment | null
  organization_id?: number | null
  /** `'disabled'` desativa; reativar é reindexar (`reindexKbDocument`), que volta a `pending` */
  status?: KbDocumentStatus
  /** Mover para outra pasta (grava `kb.document.move`) */
  folder_id?: number
}

/** Nó da árvore de pastas. `sensivel` vem `null` quando a pessoa não pode sabê-lo. */
export interface KbFolderNode {
  id: number
  nome: string
  parent_id: number | null
  /** `null` = pasta da plataforma (D16) */
  organization_id: number | null
  /** Perfil da pessoa na pasta: pode ler os documentos dela */
  liberada: boolean
  sensivel: boolean | null
}

export interface KbFolderDocument {
  id: number
  titulo: string
  status: KbDocumentStatus
  fonte: string | null
  mime: string
  paginas: number | null
  created_at: string
}

/** `descricao` e `documentos` só vêm quando a pasta é liberada. */
export interface KbFolder extends KbFolderNode {
  /** "A › B › C" */
  caminho: string | null
  subpastas: KbFolderNode[]
  descricao?: string | null
  documentos?: KbFolderDocument[]
  /** Atalhos para documentos de outras pastas; só vêm quando a pasta é liberada */
  atalhos?: KbAtalho[]
}

export interface KbFolderPerfil {
  id: number
  nome: string | null
}

export interface KbFolderNivel {
  id: number
  nome: string
  profundidade: number
  sensivel: boolean
  perfis: KbFolderPerfil[]
  /** Contagem, nunca títulos: `audit` não vê títulos (spec §5.1) */
  documentos: number
}

export interface KbFolderDetalhes {
  id: number
  nome: string
  caminho: string | null
  descricao: string | null
  sensivel: boolean
  niveis: KbFolderNivel[]
}

export interface KbFolderCreate {
  organization_id: number | null
  parent_id: number | null
  nome: string
  descricao?: string | null
  sensivel?: boolean
  template_ids?: number[]
}

export interface KbFolderPatch {
  nome?: string
  descricao?: string | null
  sensivel?: boolean
  parent_id?: number | null
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

/**
 * Faz upload de um documento para a pasta `folderId`. A organização sai da pasta,
 * no backend. O FormData deve conter:
 *   - file: File
 *   - titulo: string
 *   - descricao?: string
 *   - environment?: KbEnvironment
 *   - tags?: string (separadas por vírgula — o backend faz `split(',')`)
 *
 * O Content-Type multipart/form-data é definido automaticamente pelo browser.
 *
 * Arquivo que já está na organização volta 409 com
 * `detail: {code: 'documento_ja_existe', situacao, documento}`, a mesma forma do
 * `verificarKbDocumento`; quem lê esse erro é `repetidoDoErro` (`pages/conhecimento/envio.ts`).
 */
export async function uploadKbDocument(
  folderId: number,
  formData: FormData,
  onProgresso?: (fracao: number) => void,
): Promise<KbEnvioAceito> {
  formData.set('folder_id', String(folderId))
  const { data } = await api.post<KbEnvioAceito>('/v1/assistant/kb/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Com 100 MB, esperar sem retorno parece travamento (spec 2026-10-21, O6).
    onUploadProgress: onProgresso
      ? (e) => { if (e.total) onProgresso(Math.min(1, e.loaded / e.total)) }
      : undefined,
  })
  return data
}

export interface KbEnvioAceito {
  document_id: number
  status: string
}

/**
 * Em que situação está o arquivo de conteúdo `sha256` para a pasta `folderId`. Não muda
 * nada nem grava evento; o envio confere de novo no servidor.
 */
export async function verificarKbDocumento(folderId: number, sha256: string): Promise<KbVerificacao> {
  const { data } = await api.post<KbVerificacao>('/v1/assistant/kb/documents/verificar', {
    sha256,
    folder_id: folderId,
  })
  return data
}

/** Faz o documento aparecer também na pasta `folderId` (grava `kb.atalho.create`). */
export async function criarKbAtalho(documentId: number, folderId: number): Promise<KbAtalhoCriado> {
  const { data } = await api.post<KbAtalhoCriado>('/v1/assistant/kb/atalhos', {
    document_id: documentId,
    folder_id: folderId,
  })
  return data
}

/**
 * Onde mais o documento aparece, para a confirmação de excluir o original. `manage` na
 * organização do documento; não depende de ler a pasta dele (quem gere exclui sem ler).
 */
export async function listarAtalhosDoDocumento(documentId: number): Promise<KbAtalhoDoDocumento[]> {
  const { data } = await api.get<{ items: KbAtalhoDoDocumento[] }>(`/v1/assistant/kb/documents/${documentId}/atalhos`)
  return data.items
}

/** Tira o atalho da pasta; o original fica onde está (grava `kb.atalho.delete`). */
export async function removerKbAtalho(id: number): Promise<void> {
  await api.delete(`/v1/assistant/kb/atalhos/${id}`)
}

export interface KbLimites { max_upload_mb: number }

/** O teto de envio que a api aplica (`KB_MAX_UPLOAD_MB`). */
export async function getKbLimites(): Promise<KbLimites> {
  const { data } = await api.get<KbLimites>('/v1/assistant/kb/limites')
  return data
}

/** Lista documentos com filtros opcionais por organização, ambiente, status e tag. */
export async function listKbDocuments(
  filters?: KbDocumentFilters,
): Promise<KbDocumentListResponse> {
  const params: Record<string, string> = {}
  if (filters?.organization_id != null) params.organization_id = String(filters.organization_id)
  if (filters?.environment != null) params.environment = filters.environment
  if (filters?.status != null) params.status = filters.status
  if (filters?.tag != null) params.tag = filters.tag
  if (filters?.folder_id != null) params.folder_id = String(filters.folder_id)
  const { data } = await api.get<KbDocumentListResponse>('/v1/assistant/kb/documents', { params })
  return data
}

/** Retorna o detalhe de um documento com status e contagem de chunks. */
export async function getKbDocument(id: number): Promise<KbDocument> {
  const { data } = await api.get<KbDocument>(`/v1/assistant/kb/documents/${id}`)
  return data
}

/**
 * Retorna a URL pré-assinada do S3 para download do documento original.
 * A URL expira em `expires_in` segundos.
 */
/** Nome do arquivo pelo `Content-Disposition`, preferindo o `filename*` em UTF-8. */
export function nomeDoArquivo(cd: string, padrao: string): string {
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd)
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1])
    } catch {
      /* cai no ASCII */
    }
  }
  return /filename="?([^";]+)"?/i.exec(cd)?.[1] ?? padrao
}

/** Texto de um blob; `FileReader` onde `Blob.text` não existe (jsdom, navegador antigo). */
function textoDoBlob(b: Blob): Promise<string> {
  if (typeof b.text === 'function') return b.text()
  return new Promise((ok, falha) => {
    const leitor = new FileReader()
    leitor.onload = () => ok(String(leitor.result ?? ''))
    leitor.onerror = () => falha(leitor.error)
    leitor.readAsText(b)
  })
}

async function baixarPdf(url: string, padrao: string): Promise<void> {
  // Blob pelo cliente com o Bearer: a sessão é por Bearer, não por cookie, e não há URL
  // assinada do original em caminho nenhum (spec §9, D2). 10 min: com originais de até
  // 100 MB, o PDF de saída tem ~100 MB, e 95 s abortava no meio numa conexão comum.
  let r
  try {
    r = await api.get(url, { responseType: 'blob', timeout: 600000 })
  } catch (e) {
    // Com `responseType: 'blob'` o corpo do erro também chega como blob: sem lê-lo, o
    // `detail` (evidencia_indisponivel, documento_ilegivel) nunca chega à tela.
    const data = (e as { response?: { data?: unknown } })?.response?.data
    if (data instanceof Blob) {
      try {
        const corpo = JSON.parse(await textoDoBlob(data)) as { detail?: unknown }
        if (typeof corpo?.detail === 'string') Object.assign(e as object, { detalhe: corpo.detail })
      } catch {
        // corpo que não é JSON: fica só o status
      }
    }
    throw e
  }
  const cd = (r.headers?.['content-disposition'] as string | undefined) ?? ''
  const href = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = href
  a.download = nomeDoArquivo(cd, padrao)
  a.click()
  setTimeout(() => URL.revokeObjectURL(href), 1000)
}

/** O documento inteiro em PDF no padrão, com a marca de quem baixa. 403 para quem só lê. */
export function baixarKbInteiro(id: number): Promise<void> {
  return baixarPdf(`/v1/assistant/kb/documents/${id}/download`, `documento-${id}.pdf`)
}

/** Primeiro e último terço em PDF no padrão. 422 para documento de 1 ou 2 páginas. */
export function baixarKbEvidencia(id: number): Promise<void> {
  return baixarPdf(`/v1/assistant/kb/documents/${id}/evidencia`, `documento-${id}-evidencia.pdf`)
}

/** Metadados do visualizador. 404 igual para inexistente e sem acesso. */
export async function getKbVisualizacao(id: number): Promise<KbVisualizacao> {
  const { data } = await api.get<KbVisualizacao>(`/v1/assistant/kb/documents/${id}/visualizacao`)
  return data
}

/**
 * Uma página em imagem (WEBP com a marca d'água de quem vê). Vem pelo cliente com o
 * Bearer, como blob: não fica URL da imagem para abrir fora da sessão.
 */
export async function getKbPagina(id: number, pagina: number): Promise<Blob> {
  const { data } = await api.get<Blob>(`/v1/assistant/kb/documents/${id}/paginas/${pagina}`, {
    responseType: 'blob',
  })
  return data
}

/** Atualiza metadados de um documento (título, tags, ambiente, status, pasta). */
export async function updateKbDocument(id: number, patch: KbDocumentPatch): Promise<KbDocument> {
  const { data } = await api.patch<KbDocument>(`/v1/assistant/kb/documents/${id}`, patch)
  return data
}

/** Solicita reindexação do documento (marca como pending e dispara o worker). */
export async function reindexKbDocument(id: number): Promise<{ queued: boolean }> {
  const { data } = await api.post<{ queued: boolean }>(
    `/v1/assistant/kb/documents/${id}/reindex`,
  )
  return data
}

/** Remove o documento, seus chunks e o arquivo do S3 (best-effort). */
export async function deleteKbDocument(id: number): Promise<void> {
  await api.delete(`/v1/assistant/kb/documents/${id}`)
}

// ---------------------------------------------------------------------------
// Pastas
// ---------------------------------------------------------------------------

/** Árvore que a pessoa pode navegar; quem gere ou audita recebe a árvore inteira. */
export async function listKbFolders(organizationId?: number | null): Promise<KbFolderNode[]> {
  const params: Record<string, string> = {}
  if (organizationId != null) params.organization_id = String(organizationId)
  const { data } = await api.get<{ items: KbFolderNode[] }>('/v1/assistant/kb/folders', { params })
  return data.items
}

export interface KbOrgRotulo {
  organization_id: number
  rotulo: string
}

/** Nome com que cada organização aparece no primeiro nível da árvore (só na KB). */
export async function listKbRotulos(): Promise<KbOrgRotulo[]> {
  const { data } = await api.get<{ items: KbOrgRotulo[] }>('/v1/assistant/kb/rotulos')
  return data.items
}

/** `null` ou vazio volta ao nome da organização. */
export async function setKbRotulo(organizationId: number, rotulo: string | null): Promise<void> {
  await api.put(`/v1/assistant/kb/rotulos/${organizationId}`, { rotulo })
}

/** Empresa acima das organizações na árvore (spec 2026-10-21-kb-raizes, R1-R8). */
export interface KbRaiz {
  id: number
  nome: string
  ordem: number
  /** A única que recebe as pastas sem organização */
  plataforma: boolean
}

export type KbSecao = 'topo' | 'projetos'

export interface KbLugar {
  raiz_id: number
  secao: KbSecao
}

export interface KbRaizes {
  /** Só as que a pessoa pode saber que existem */
  items: KbRaiz[]
  /** Só organizações com lugar gravado; as outras ficam em `padrao` */
  organizacoes: (KbLugar & { organization_id: number })[]
  padrao: KbLugar | null
}

export async function listKbRaizes(): Promise<KbRaizes> {
  const { data } = await api.get<KbRaizes>('/v1/assistant/kb/raizes')
  return data
}

export async function createKbRaiz(nome: string, ordem: number): Promise<KbRaiz> {
  const { data } = await api.post<KbRaiz>('/v1/assistant/kb/raizes', { nome, ordem })
  return data
}

export async function renameKbRaiz(id: number, nome: string): Promise<KbRaiz> {
  const { data } = await api.patch<KbRaiz>(`/v1/assistant/kb/raizes/${id}`, { nome })
  return data
}

export async function setKbOrgLugar(organizationId: number, lugar: KbLugar): Promise<void> {
  await api.put(`/v1/assistant/kb/organizations/${organizationId}/raiz`, lugar)
}

export async function getKbFolder(id: number): Promise<KbFolder> {
  const { data } = await api.get<KbFolder>(`/v1/assistant/kb/folders/${id}`)
  return data
}

/** Subárvore com perfis e contagens — `manage`, `audit` ou admin. */
export async function getKbFolderDetalhes(id: number): Promise<KbFolderDetalhes> {
  const { data } = await api.get<KbFolderDetalhes>(`/v1/assistant/kb/folders/${id}/detalhes`)
  return data
}

export async function createKbFolder(body: KbFolderCreate): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>('/v1/assistant/kb/folders', body)
  return data
}

export async function updateKbFolder(id: number, patch: KbFolderPatch): Promise<void> {
  await api.patch(`/v1/assistant/kb/folders/${id}`, patch)
}

/** Substitui a lista de perfis da pasta (grava `kb.folder.access` com antes e depois). */
export async function setKbFolderPerfis(id: number, templateIds: number[]): Promise<void> {
  await api.put(`/v1/assistant/kb/folders/${id}/perfis`, { template_ids: templateIds })
}

/** Só pasta vazia; com conteúdo o backend responde 409 `folder_not_empty`. */
export async function deleteKbFolder(id: number): Promise<void> {
  await api.delete(`/v1/assistant/kb/folders/${id}`)
}

// ---------------------------------------------------------------------------
// Fontes do KB-Git
// ---------------------------------------------------------------------------

export async function listKbGitSources(organizationId?: number | null): Promise<KbGitSource[]> {
  const params: Record<string, string> = {}
  if (organizationId != null) params.organization_id = String(organizationId)
  const { data } = await api.get<{ items: KbGitSource[] }>('/v1/assistant/kb/git/sources', { params })
  return data.items
}

/** `reativar` só de `excluido` e `regerar` só fora dele; o contrário é 409. */
export async function mudarKbGitSource(
  id: number,
  operacao: KbGitSourceOperacao,
): Promise<{ id: number; estado: KbGitSourceEstado; alterado: boolean }> {
  const { data } = await api.post<{ id: number; estado: KbGitSourceEstado; alterado: boolean }>(
    `/v1/assistant/kb/git/sources/${id}/${operacao}`,
  )
  return data
}

// ---------------------------------------------------------------------------
// Fontes consultadas pelo @Atendente (spec 2026-09-26-atendente-fontes §7)
// ---------------------------------------------------------------------------

/** Título de uma fonte PARA QUEM LÊ: `null` quando não lê, foi removido ou não existe. */
export interface KbFonteResolvida {
  document_id: number
  titulo: string | null
  disponivel: boolean
}

/** Teto de ids distintos da rota (o mesmo da mensagem, `_FONTES_MAX_NA_MENSAGEM`). */
export const FONTES_MAX_POR_CHAMADA = 40

/**
 * GET /assistant/kb/fontes?ids=…, em lotes de 40.
 *
 * A query é montada com `URLSearchParams.append`, não com `params: { ids }`: o
 * axios serializa array como `ids[]=1`, que o FastAPI não lê como `ids` — a rota
 * responderia 200 com lista vazia e toda fonte pareceria indisponível.
 */
export async function resolverFontesKb(ids: number[]): Promise<KbFonteResolvida[]> {
  const distintos = Array.from(new Set(ids))
  const lotes: number[][] = []
  for (let i = 0; i < distintos.length; i += FONTES_MAX_POR_CHAMADA) {
    lotes.push(distintos.slice(i, i + FONTES_MAX_POR_CHAMADA))
  }
  const respostas = await Promise.all(
    lotes.map(async (lote) => {
      const qs = new URLSearchParams()
      for (const id of lote) qs.append('ids', String(id))
      const { data } = await api.get<{ items: KbFonteResolvida[] }>(
        `/v1/assistant/kb/fontes?${qs.toString()}`,
      )
      return data.items
    }),
  )
  return respostas.flat()
}

// ---------------------------------------------------------------------------
// Busca por nome e conteúdo (spec 2026-10-20-kb-busca, §4)
// ---------------------------------------------------------------------------

export interface KbBuscaTrecho {
  pagina: number | null
  /** Texto puro, até 280 caracteres; o destaque é feito na tela */
  texto: string
  /** Algum termo da busca aparece no trecho (sem acento). Falso = só parecido: "Relacionado" */
  contem_termo: boolean
}

export interface KbBuscaDocumento {
  document_id: number
  titulo: string
  fonte: string | null
  organization_id: number | null
  pasta_id: number | null
  pasta_caminho: string | null
  status: KbDocumentStatus
  /** Todos os termos aparecem no título ou na fonte */
  pelo_nome: boolean
  trechos: KbBuscaTrecho[]
}

export interface KbBusca {
  q: string
  documentos: KbBuscaDocumento[]
  /** `indisponivel`: o conteúdo não respondeu e só os nomes vieram */
  conteudo: 'ok' | 'indisponivel' | 'sem_resultado'
}

/** GET /assistant/kb/busca — `q` com 3 caracteres ou mais (o backend responde 422 abaixo disso). */
export async function buscarNaKb(q: string, signal?: AbortSignal): Promise<KbBusca> {
  const { data } = await api.get<KbBusca>('/v1/assistant/kb/busca', { params: { q }, signal })
  return data
}
